import React, { useState } from "react";
import { IconButton, Modal, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DEFAULT_POST_CONTENT_CHAR_LIMIT } from "../../Helpers/constants";
import {
  useCreatePostMutation,
  useUploadPostFilesMutation,
} from "../misc/mutations";
import Post from "./Post";

interface TextInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isContentLimitExceeded: boolean;
  fullHeight?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({
  value,
  onChange,
  isContentLimitExceeded,
  fullHeight,
}) => (
  <div
    className={`px-2 w-full relative ${fullHeight && "basis-full h-full"} mt-2`}
  >
    <textarea
      value={value}
      onChange={onChange}
      className={`p-2 border rounded-lg h-full w-full text-start resize-none focus:outline-none ${
        isContentLimitExceeded ? "border-red-500" : "border-gray-300"
      }`}
      rows={fullHeight ? undefined : 1}
      placeholder="Your post content..."
    />
    <div className="absolute bottom-2 right-4 text-gray-500">
      {value.length}/{DEFAULT_POST_CONTENT_CHAR_LIMIT}
    </div>
  </div>
);

interface DroppedFilesListProps {
  files: File[];
  removeFile: (index: number) => void;
}

const DroppedFilesList: React.FC<DroppedFilesListProps> = ({
  files,
  removeFile,
}) => {
  const [showDelete, setShowDelete] = useState<number>(-1);

  return (
    <div className="basis-2/12 flex flex-row w-full p-2 gap-2">
      {files.map((file, index) => (
        <div
          key={index}
          className="bg-slate-200 rounded cursor-pointer items-start justify-center relative"
          onMouseEnter={() => setShowDelete(index)}
          onMouseLeave={() => setShowDelete(-1)}
        >
          <Tooltip title={file.name}>
            {showDelete === index ? (
              <IconButton
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-12 w-12"
                onClick={() => removeFile(index)}
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            ) : file.type.startsWith("image/") ? (
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className="h-12 w-12 object-contain"
              />
            ) : (
              <div className="bg-gray-200 h-12 w-12 flex items-center justify-center">
                {file.name.split(".").pop()} {/* Show file extension */}
              </div>
            )}
          </Tooltip>
        </div>
      ))}
    </div>
  );
};

interface TopBarProps {
  showShare: boolean;
  shareIsDisabled: boolean;
  handleCreate: () => void;
  handleClose: () => void;
}

const TopBar: React.FC<TopBarProps> = ({
  showShare,
  shareIsDisabled,
  handleCreate,
  handleClose,
}) => (
  <div className="flex flex-row items-center justify-between w-full px-2 basis-1/12">
    <div className="flex flex-row items-start justify-center gap-2">
      <div className="font-semibold text-gray-600">Information</div>
      {/* <div>Post types go here.</div> */}
    </div>
    <div className="flex flex-row items-end justify-center gap-2">
      <button
        className={`rounded-xl py-1/2 px-3 
          bg-red-300 text-red-600
          hover:text-white hover:bg-red-500
          transition-all duration-300
          `}
        onClick={handleClose}
      >
        Cancel
      </button>
      {showShare && (
        <button
          className={`rounded-xl py-1/2 px-3 
            bg-blue-300 text-blue-600
            hover:text-white hover:bg-blue-500
            disabled:opacity-50
            transition-all duration-300
            `}
          onClick={handleCreate}
          disabled={shareIsDisabled}
        >
          Share
        </button>
      )}
    </div>
  </div>
);

interface ChildModalProps {
  onClose: (confirmed: boolean) => void;
}

const ChildModal: React.FC<ChildModalProps> = ({ onClose }) => (
  <div className="fixed bg-red-100 w-fit h-fit p-4 rounded-lg flex flex-col justify-center items-center max-w-64">
    <div className="text-lg font-light text-red-700 text-center">
      You Sure?
      <br />
      you will have to reshare this information
    </div>
    <div className="flex flex-row items-center justify-center gap-2 mt-4">
      <button
        className={`rounded-xl py-1/2 px-3
          bg-gray-300 text-white
          hover:text-white hover:bg-red-500
          transition-all duration-300
          `}
        onClick={() => onClose(true)}
      >
        Yes
      </button>
      <button
        className={`rounded-xl py-1/2 px-3
          bg-blue-300 text-blue-600
          hover:text-white hover:bg-blue-500
          transition-all duration-300
          `}
        onClick={() => onClose(false)}
      >
        No
      </button>
    </div>
  </div>
);

interface MakePostProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetPosts?: () => void;
  postId?: number;
}

export const MakeNormalPost: React.FC<MakePostProps> = ({
  isOpen,
  setIsOpen,
  resetPosts,
  postId,
}) => {
  const postType = postId ? "Quote" : "Normal";
  const [postContent, setPostContent] = useState<string>("");
  const [isContentLimitExceeded, setIsContentLimitExceeded] =
    useState<boolean>(false);
  const [postFiles, setPostFiles] = useState<File[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const resetValues = () => {
    setIsOpen(false);
    setPostContent("");
    setPostFiles([]);
  };

  const uploadPostFiles = useUploadPostFilesMutation(resetValues);

  const afterCreatePost = (postId: number) => {
    if (postFiles.length) {
      const formData = new FormData();
      postFiles.forEach((file) => {
        formData.append("files", file);
      });
      uploadPostFiles.mutate({ postId: postId, files: formData });
    }
    setIsOpen(false);
    setPostContent("");
  };

  const handleClose = () => {
    if (postContent !== "" || postFiles.length > 0) {
      setShowConfirmation(true);
    } else {
      resetValues();
    }
  };

  const createPost = useCreatePostMutation(afterCreatePost, resetPosts);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const postContent = e.target.value;
    setPostContent(postContent);
    setIsContentLimitExceeded(
      postContent.length > DEFAULT_POST_CONTENT_CHAR_LIMIT,
    );
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const newFiles = Array.from(e.dataTransfer.files);
    setPostFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setPostFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleCreatePost = () => {
    createPost.mutate({
      content: postContent,
      postType: postType,
      parentPostId: postId,
    });
  };

  const handleConfirmationClose = (confirmed: boolean) => {
    setShowConfirmation(false);
    if (confirmed) {
      setPostContent("");
      setPostFiles([]);
      setIsOpen(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div
        className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-slate-100 md:w-1/2 w-5/6 ${
          postId ? "flex-grow" : "h-1/2 max-h-3/4"
        } p-4 rounded-lg flex flex-col justify-center items-center`}
        onDrop={handleFileDrop}
      >
        <TopBar
          showShare={postContent !== "" || postFiles.length !== 0}
          shareIsDisabled={isContentLimitExceeded}
          handleCreate={handleCreatePost}
          handleClose={handleClose}
        />
        <TextInput
          value={postContent}
          onChange={handleInputChange}
          isContentLimitExceeded={isContentLimitExceeded}
          fullHeight={postId ? false : true}
        />
        {/* <div className="my-6"></div> */}
        {postId && <Post internalPostId={postId} />}
        {postFiles.length !== 0 && (
          <DroppedFilesList files={postFiles} removeFile={removeFile} />
        )}
        {showConfirmation && <ChildModal onClose={handleConfirmationClose} />}
      </div>
    </Modal>
  );
};
